import { useEffect, useMemo } from 'react'
import { useT } from '../i18n/context'
import { useClients, useProjects } from '../hooks/useApi'
import { useActiveWorkspaceId } from '../stores/workspace'
import { Select } from './ui/Field'

interface ProjectOption {
    projectId: number | null
    clientId: number | null
    label: string
}

function useProjectOptions(workspaceId: number | null) {
    const clients = useClients(workspaceId)
    const projects = useProjects(workspaceId)
    return useMemo(() => {
        const clientName = new Map(
            (clients.data ?? []).map(c => [c.id, c.name]),
        )
        const options: ProjectOption[] = (projects.data ?? []).map(p => ({
            projectId: p.id,
            clientId: p.clientId,
            label: `${clientName.get(p.clientId) ?? '?'} / ${p.name}`,
        }))
        return {
            options,
            isLoading: clients.isLoading || projects.isLoading,
            clientNames: clientName,
        }
    }, [clients.data, clients.isLoading, projects.data, projects.isLoading])
}

/**
 * Auswahl für Zeiteinträge: Projekt (mit Kunde), nur Kunde oder
 * „ohne Zuordnung". onChange liefert immer das paarweise Ergebnis.
 */
export function ProjectSelect({
    value,
    onChange,
    disabled,
}: {
    value: { projectId: number | null; clientId: number | null }
    onChange: (projectId: number | null, clientId: number | null) => void
    disabled?: boolean
}) {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const { options, isLoading, clientNames } = useProjectOptions(workspaceId)

    const entryOptions = useMemo(() => {
        const clientOnly = [...clientNames.entries()].map(
            ([clientId, name]) => ({
                projectId: null as number | null,
                clientId: clientId as number | null,
                label: t('projectSelect.withoutProject', { name }),
            }),
        )
        return [
            {
                projectId: null as number | null,
                clientId: null as number | null,
                label: t('projectSelect.withoutAssignment'),
            },
            ...options,
            ...clientOnly,
        ]
    }, [options, clientNames, t])

    const optionKey = (o: {
        projectId: number | null
        clientId: number | null
    }) =>
        o.projectId !== null
            ? `p${o.projectId}`
            : o.clientId !== null
              ? `c${o.clientId}`
              : 'none'

    const selected = entryOptions.find(
        o =>
            o.projectId === value.projectId &&
            (o.projectId !== null || o.clientId === value.clientId),
    )
    const hasSelection = selected !== undefined

    // Persistierte Auswahl kann aus einem anderen Workspace stammen und
    // wäre dann nicht startbar — auf „ohne Zuordnung" zurücksetzen.
    useEffect(() => {
        if (isLoading || hasSelection) return
        if (value.projectId === null && value.clientId === null) return
        onChange(null, null)
    }, [
        isLoading,
        hasSelection,
        value.projectId,
        value.clientId,
        onChange,
    ])

    return (
        <Select
            // Screenreader-Name — das Select sitzt neben dem
            // Beschreibungs-Input ohne sichtbares Label (select-name)
            aria-label={t('projectSelect.label')}
            value={selected ? optionKey(selected) : 'none'}
            onChange={e => {
                const v = e.target.value
                if (v === 'none') return onChange(null, null)
                const opt = entryOptions.find(o => optionKey(o) === v)
                onChange(opt?.projectId ?? null, opt?.clientId ?? null)
            }}
            disabled={disabled || isLoading}
        >
            {entryOptions.map(o => (
                <option key={optionKey(o)} value={optionKey(o)}>
                    {o.label}
                </option>
            ))}
        </Select>
    )
}
